/**
 * Browser entry point.
 *
 * Wires the canvas, the game controller and the window-level concerns (resize,
 * visibility, audio unlock, touch detection) that should not live inside the
 * game itself.
 */

import { Game } from './game.js';

function fail(message: string): void {
  const status = document.getElementById('boot-status');
  if (status) {
    status.textContent = message;
    status.className = 'boot-error';
  }
  console.error('[apogee]', message);
}

function start(): void {
  const canvas = document.getElementById('screen');
  if (!(canvas instanceof HTMLCanvasElement)) {
    fail('The game canvas is missing from the page.');
    return;
  }

  let game: Game;
  try {
    game = new Game(document.body, canvas);
  } catch (err) {
    fail(err instanceof Error ? err.message : 'Could not initialise the renderer.');
    return;
  }

  // The HUD is canvas drawn, so the live region is how a screen-reader user
  // learns that the game is ready.
  const live = document.getElementById('live-region');
  const announce = (text: string): void => {
    if (live) live.textContent = text;
  };

  const onResize = (): void => game.resize();
  window.addEventListener('resize', onResize);
  window.addEventListener('orientationchange', onResize);
  if (typeof ResizeObserver !== 'undefined') {
    new ResizeObserver(onResize).observe(document.body);
  }

  // Browsers only allow audio after a gesture; the first interaction unlocks
  // the context for the rest of the session.
  const unlock = (): void => game.audio.unlock();
  window.addEventListener('pointerdown', unlock);
  window.addEventListener('keydown', unlock);

  document.addEventListener('visibilitychange', () => {
    game.onVisibilityChange(document.hidden);
  });

  // Touch devices get on-screen controls automatically.
  if ('ontouchstart' in window || navigator.maxTouchPoints > 0) {
    game.setTouchControls(true);
  }

  // Stop Safari's pinch-zoom gesture from fighting the canvas.
  document.addEventListener('gesturestart', (e) => e.preventDefault(), { passive: false });

  // Handy for support: `__apogee` exposes the live controller in devtools.
  (window as unknown as { __apogee: Game }).__apogee = game;

  void game.boot().then(() => {
    announce('Apogee Racer ready.');
  });
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', start, { once: true });
} else {
  start();
}
